import { Slot } from './Slot'
import { Tabular } from './Tabular'
import type { VariantProps } from '@joyful/utils'
import {
  cn,
  tv,
  focusRingClassName,
  formatNumber,
  createNonNullableContext,
} from '@joyful/utils'
import type { ComponentProps, ReactNode, RefAttributes } from 'react'

export const itemVariants = tv({
  base: 'w-full relative flex items-center gap-2 rounded-sm text-tremor-content-strong outline-hidden min-w-0',

  compoundVariants: [
    {
      className: 'hover:bg-tremor-background-subtle',
      isInteractive: true,
      isSelected: false,
    },
    {
      className: 'bg-tremor-brand-faint',
      isSelected: true,
      selectionVariant: 'brand',
    },
    {
      className: 'focus:bg-tremor-brand-faint',
      isInteractive: true,
      isSelected: true,
      selectionVariant: 'brand',
    },
    {
      className: 'bg-tremor-background-subtle border-tremor-border',
      isSelected: true,
      selectionVariant: ['emphasis', 'muted'],
    },
    {
      className: 'focus:bg-tremor-background-subtle focus:border-tremor-border',
      isInteractive: true,
      isSelected: true,
      selectionVariant: ['emphasis', 'muted'],
    },
    {
      className: {
        base: 'text-tremor-content-emphasis',
        icon: 'text-tremor-content',
      },
      isSelected: false,
      selectionVariant: 'emphasis',
    },
    {
      className: { icon: 'text-tremor-content-strong' },
      isSelected: true,
      selectionVariant: 'emphasis',
    },
    {
      className: focusRingClassName,
      isInteractive: true,
      isSelected: true,
    },
  ],

  defaultVariants: {
    animation: 'default',
    colorVariant: 'default',
    isInset: false,
    isInteractive: false,
    isSelected: false,
    selectionVariant: 'brand',
    size: 'md',
  },

  slots: {
    container:
      'flex min-w-0 flex-[1_0_0] flex-col items-start overflow-hidden text-current',
    endAdornment: 'ml-2 inline-flex shrink-0 items-center',
    icon: 'size-4',
    subtitle: 'text-tremor-content-subtle max-w-full',
  },

  variants: {
    align: {
      start: {
        base: 'items-start',
        endAdornment: 'mt-0.5',
        icon: 'mt-0.5',
      },
    },

    animation: {
      default: 'transition-colors',
      none: null,
    },

    colorVariant: {
      default: null,
      destructive: 'text-red-600 dark:text-red-400',
    },

    isInset: {
      true: {
        base: 'pl-8!',
      },
    },

    isInteractive: {
      true: {
        base: `cursor-pointer select-none border border-transparent bg-transparent text-left focus:bg-tremor-background-subtle data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-tremor-background-subtle data-[state=open]:bg-tremor-background-subtle data-[state=open]:border-tremor-border`,
      },
    },
    isSelected: {
      true: null,
    },
    padding: {
      none: 'px-0 py-0',
    },
    selectionVariant: {
      brand: null,
      emphasis: null,
      muted: null,
    },
    size: {
      lg: {
        base: 'px-2.5 py-2.5 text-sm',
        container: 'text-sm',
        subtitle: 'text-xs',
      },
      md: {
        base: 'px-2 py-1.5 text-sm',
        container: 'text-sm',
        subtitle: 'text-xs',
      },
      sm: {
        base: 'px-2 py-1.5 text-xs',
        container: 'text-xs',
        subtitle: 'text-xs',
      },
      xl: {
        base: 'px-3 py-3',
        container: 'gap-0.5',
        subtitle: 'text-xs',
      },
    },
  },
})

const itemSubtitleVariants = tv({
  variants: {
    tone: {
      content: 'text-tremor-content',
      inherit: 'text-inherit',
    },
    weight: {
      medium: 'font-medium',
    },
    wrap: {
      truncate: 'truncate',
    },
  },
})

const ItemClassNamesContext = createNonNullableContext<
  ReturnType<typeof itemVariants>
>('ItemClassNamesContext')

export interface ItemProps
  extends
    Omit<ComponentProps<typeof Slot>, 'className'>,
    RefAttributes<HTMLElement>,
    VariantProps<typeof itemVariants> {
  /**
   * @default false
   */
  asChild?: boolean
  type?: 'button' | 'submit' | 'reset'
}

export function Item({
  align,
  asChild = false,
  children,
  colorVariant,
  isInset,
  isInteractive,
  isSelected,
  padding,
  selectionVariant,
  size,
  type,
  ...props
}: ItemProps) {
  const classNames = itemVariants({
    align,
    colorVariant,
    isInset,
    isInteractive,
    isSelected,
    padding,
    selectionVariant,
    size,
  })

  const Comp = asChild ? Slot : isInteractive ? 'button' : 'div'
  const elementProps =
    isInteractive && !asChild ? { ...props, type: type ?? 'button' } : props
  return (
    <ItemClassNamesContext.Context value={classNames}>
      {/* @ts-expect-error - cannot forward ref to slot */}
      <Comp className={classNames.base()} {...elementProps}>
        {children}
      </Comp>
    </ItemClassNamesContext.Context>
  )
}

export type ItemContentProps = Omit<ComponentProps<'div'>, 'className'>

export function ItemContent({ children, ...props }: ItemContentProps) {
  const classNames = ItemClassNamesContext.useRequiredContext()

  return (
    <div className={classNames.container()} {...props}>
      {children}
    </div>
  )
}

export type ItemIconProps = Omit<ComponentProps<typeof Slot>, 'className'>

export function ItemIcon(props: ItemIconProps) {
  const classNames = ItemClassNamesContext.useRequiredContext()

  return <Slot className={classNames.icon()} {...props} />
}

export interface ItemSubtitleProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof itemSubtitleVariants> {
  /**
   * @default false
   */
  asChild?: boolean
}

export function ItemSubtitle({
  asChild = false,
  children,
  tone,
  weight,
  wrap,
  ...props
}: ItemSubtitleProps) {
  const Comp = asChild ? Slot : 'p'
  const classNames = ItemClassNamesContext.useRequiredContext()

  return (
    <Comp
      className={cn(
        classNames.subtitle(),
        itemSubtitleVariants({ tone, weight, wrap }),
      )}
      {...props}
    >
      {children}
    </Comp>
  )
}

export type ItemEndAdornmentProps = Omit<ComponentProps<'span'>, 'className'>

export function ItemEndAdornment(props: ItemEndAdornmentProps) {
  const classNames = ItemClassNamesContext.useRequiredContext()

  return <span className={classNames.endAdornment()} {...props} />
}

const itemSeparatorVariants = tv({
  base: '-mx-1 my-1 bg-tremor-border',

  defaultVariants: {
    thickness: 'thin',
  },

  variants: {
    thickness: {
      thick: 'h-0.5',
      thin: 'h-px',
    },
  },
})

export interface ItemSeparatorProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof itemSeparatorVariants> {
  /**
   * @default false
   */
  asChild?: boolean
}

export function ItemSeparator({
  asChild = false,
  thickness,
  ...props
}: ItemSeparatorProps) {
  const Comp = asChild ? Slot : 'div'
  return <Comp className={itemSeparatorVariants({ thickness })} {...props} />
}

const itemGroupVariants = tv({
  base: 'flex flex-col px-2 py-0.5',

  variants: {
    gap: {
      xs: 'gap-y-0.5',
    },
    padding: {
      none: 'px-0 py-0',
    },
  },
})

export interface ItemGroupProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof itemGroupVariants> {
  /**
   * @default false
   */
  asChild?: boolean
}

export function ItemGroup({
  asChild = false,
  gap,
  padding,
  ...props
}: ItemGroupProps) {
  const Comp = asChild ? Slot : 'div'
  return <Comp className={itemGroupVariants({ gap, padding })} {...props} />
}

const itemGroupLabelVariants = tv({
  base: 'text-tremor-content-subtle text-xs font-medium',

  compoundVariants: [
    {
      className: 'pl-8',
      isInset: true,
      size: 'md',
    },
  ],

  defaultVariants: {
    isInset: false,
  },

  variants: {
    isInset: {
      true: 'pl-8',
    },

    padding: {
      compact: 'px-2 py-1.5',
    },

    size: {
      md: 'px-2 py-1 text-base',
      sm: 'text-sm',
    },

    tone: {
      content: 'text-tremor-content',
      emphasis: 'text-tremor-content-emphasis',
    },

    weight: {
      semibold: 'font-semibold',
    },
  },
})
export interface ItemGroupLabelProps
  extends
    Omit<ComponentProps<'p'>, 'className'>,
    VariantProps<typeof itemGroupLabelVariants> {
  /**
   * @default false
   */
  asChild?: boolean
}

export function ItemGroupLabel({
  asChild = false,
  isInset,
  padding,
  size,
  tone,
  weight,
  ...props
}: ItemGroupLabelProps) {
  const Comp = asChild ? Slot : 'p'
  return (
    <Comp
      className={itemGroupLabelVariants({
        isInset,
        padding,
        size,
        tone,
        weight,
      })}
      {...props}
    />
  )
}

const itemAdornmentBadgeVariants = tv({
  base: 'dark:bg-tremor-background-subtle rounded-md bg-gray-200 whitespace-nowrap',

  defaultVariants: {
    size: 'md',
  },

  variants: {
    size: {
      md: 'px-2 py-1',
      sm: 'px-1.5 py-0.5',
    },
    variant: {
      muted: 'bg-tremor-background-subtle',
    },
  },
})

export interface ItemAdornmentBadgeProps
  extends
    Omit<ComponentProps<'span'>, 'className'>,
    VariantProps<typeof itemAdornmentBadgeVariants> {
  children: number | ReactNode
}

export function ItemAdornmentBadge({
  children,
  size,
  variant,
  ...props
}: ItemAdornmentBadgeProps) {
  return (
    <span className={itemAdornmentBadgeVariants({ size, variant })} {...props}>
      <Tabular size="xs" tone="emphasis" weight="medium">
        {typeof children === 'number' ? formatNumber(children) : children}
      </Tabular>
    </span>
  )
}
